import { Injectable, OnDestroy } from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { DsBreakpoints, DsModalRef, DsModalService, DsModalVariant } from '@levelaccess/design-system';
import { AiChatModalComponent } from './ai-chat-modal/ai-chat-modal.component';
import { AiChatStateService } from './ai-chat-state.service';
import { WindowService } from '../../services/window.service';

@Injectable({
  providedIn: 'root',
})
export class AiChatMobileModalService implements OnDestroy {
  private subscriptions: Subscription = new Subscription();
  private currentModalRef: DsModalRef | null = null;
  private isViewportSwitch: boolean = false;

  constructor(
    private aiChatStateService: AiChatStateService,
    private windowService: WindowService,
    private modalService: DsModalService,
  ) {
    this.syncMobileModalWithState();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private syncMobileModalWithState(): void {
    this.subscriptions.add(
      combineLatest([
        this.aiChatStateService.isChatOpen$,
        this.windowService.observeBreakpoint(DsBreakpoints.Medium).pipe(distinctUntilChanged()),
      ]).subscribe(([isOpen, isMobile]: [boolean, boolean]) => {
        const shouldShowMobileModal = isOpen && isMobile;

        if (shouldShowMobileModal && !this.currentModalRef) {
          this.openMobileModal();
        } else if (!shouldShowMobileModal && this.currentModalRef) {
          this.closeMobileModal();
        }
      }),
    );
  }

  private openMobileModal(): void {
    this.aiChatStateService.hideChatHistory();
    this.currentModalRef = this.modalService.open(AiChatModalComponent, DsModalVariant.slideOut) ?? null;

    if (this.currentModalRef) {
      this.subscriptions.add(this.currentModalRef.closed.subscribe(() => this.handleModalClosed()));
      this.subscriptions.add(this.currentModalRef.dismissed.subscribe(() => this.handleModalClosed()));
    }
  }

  private handleModalClosed(): void {
    const closedByUser = !this.isViewportSwitch;
    this.currentModalRef = null;

    if (closedByUser) {
      this.aiChatStateService.closeAiChatPanel();
    }
  }

  private closeMobileModal(): void {
    if (this.currentModalRef) {
      this.isViewportSwitch = true;
      this.modalService.closeCurrentModal();
      this.currentModalRef = null;
      this.isViewportSwitch = false;
    }
  }
}
